import { useState, useRef, useEffect, useMemo } from "react";
import {
  Grid,
  Card,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  LinearProgress,
} from "@mui/material";
import {
  CloudUpload,
  Delete,
  Edit,
  PlayArrow,
  Stop,
  Add
} from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Pagination from "components/common/pagination/Pagination";
import VideoPlayer from "layouts/candidate-test/components/VideoPlayer";
import blockC from "layouts/candidate-test/data/blocks/blockC";
import videoFileService from "services/videoFileService";

// Context
import { useMaterialUIController } from "context";

// Material Dashboard 2 React components for table
import MDBadge from "components/MDBadge";
import PropTypes from "prop-types";

const ITEMS_PER_PAGE = 5;

const AdminVideoPanel = () => {
  const [videos, setVideos] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [newVideoFile, setNewVideoFile] = useState(null);
  const [newVideoTitle, setNewVideoTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [previewPlaying, setPreviewPlaying] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState({ columns: [], rows: [] });
  const [controller] = useMaterialUIController();
  const { searchTerm } = controller;
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCurrentVideos();
  }, []);

  const loadCurrentVideos = async () => {
    try {
      const existingVideos = await videoFileService.getExistingVideos();
      setVideos(existingVideos);
      const data = createVideoTableData(existingVideos);
      setTableData(data);
    } catch (error) {
      showSnackbar("Error cargando videos: " + error.message, "error");
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        showSnackbar("Ingresa un archivo de video válido", "error");
        return;
      }
      if (file.size > 100 * 1024 * 1024) { // 100MB limit
        showSnackbar("El archivo es muy grande. El tamaño máximo es 100MB", "error");
        return;
      }
      setNewVideoFile(file);
      setNewVideoTitle(file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleUploadVideo = async () => {
    if (!newVideoFile || !newVideoTitle.trim()) {
      showSnackbar("Selecciona un archivo", "error");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload using video file service
      const newVideo = await videoFileService.uploadVideo(
        newVideoFile, 
        newVideoTitle,
        (progress) => setUploadProgress(progress)
      );

      // Reload videos list
      await loadCurrentVideos();
      
      // Update blockC data for immediate use in tests
      blockC[0].songs.push({
        id: newVideo.id,
        videoUrl: newVideo.url
      });

      setUploading(false);
      setUploadProgress(0);
      setUploadDialogOpen(false);
      setNewVideoFile(null);
      setNewVideoTitle("");
      showSnackbar(`Video subido exitosamente`, "success");

    } catch (error) {
      setUploading(false);
      setUploadProgress(0);
      showSnackbar("Error subiendo video: " + error.message, "error");
    }
  };

  const handleEditVideo = (video) => {
    setSelectedVideo(video);
    setNewVideoTitle(video.title);
    setEditDialogOpen(true);
  };

  const handleUpdateVideo = async () => {
    if (!newVideoTitle.trim()) {
      showSnackbar("Dale un título", "error");
      return;
    }

    try {
      if (selectedVideo.isStatic) {
        showSnackbar("Cannot edit pre-existing video titles", "warning");
        return;
      }

      await videoFileService.updateVideo(selectedVideo.id, {
        title: newVideoTitle
      });
      
      await loadCurrentVideos();
      setEditDialogOpen(false);
      setSelectedVideo(null);
      setNewVideoTitle("");
      showSnackbar("Video updated successfully", "success");
    } catch (error) {
      showSnackbar("Error updating video: " + error.message, "error");
    }
  };

  const handleDeleteVideo = (video) => {
    setSelectedVideo(video);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteVideo = async () => {
    try {
      if (selectedVideo.isStatic) {
        showSnackbar("Cannot delete pre-existing videos", "warning");
        return;
      }

      await videoFileService.deleteVideo(selectedVideo.id);
      
      // Update blockC data
      blockC[0].songs = blockC[0].songs.filter(song => song.id !== selectedVideo.id);
      
      await loadCurrentVideos();
      setDeleteDialogOpen(false);
      setSelectedVideo(null);
      showSnackbar("Video deleted successfully from fragments folder", "success");
    } catch (error) {
      showSnackbar("Error deleting video: " + error.message, "error");
    }
  };

  const handlePreviewToggle = (videoId) => {
    setPreviewPlaying(previewPlaying === videoId ? null : videoId);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Components for table data
  const VideoInfo = ({ title, filename }) => (
    <MDBox lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{filename}</MDTypography>
    </MDBox>
  );

  VideoInfo.propTypes = {
    title: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
  };

  const VideoDetails = ({ size, duration }) => (
    <MDBox lineHeight={1} textAlign="left">
      <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
        {size}
      </MDTypography>
      <MDTypography variant="caption">{duration}</MDTypography>
    </MDBox>
  );

  VideoDetails.propTypes = {
    size: PropTypes.string.isRequired,
    duration: PropTypes.string.isRequired,
  };

  const getVideoBadge = (isStatic) => {
    const config = isStatic 
      ? { color: "secondary", label: "Original" }
      : { color: "success", label: "Nuevo" };
    
    return (
      <MDBox ml={-1}>
        <MDBadge badgeContent={config.label} color={config.color} variant="gradient" size="sm" />
      </MDBox>
    );
  };

  const createVideoTableData = (videosData) => {
    return {
      columns: [
        { Header: "video", accessor: "video", width: "30%", align: "left" },
        { Header: "detalles", accessor: "detalles", align: "left" },
        { Header: "fecha", accessor: "fecha", align: "center" },
        { Header: "tipo", accessor: "tipo", align: "center" },
        { Header: "acciones", accessor: "acciones", align: "center" },
      ],
      rows: videosData.map((video) => ({
        video: <VideoInfo title={video.title} filename={video.filename} />,
        detalles: <VideoDetails size={video.size} duration={video.duration} />,
        fecha: (
          <MDTypography component="span" variant="caption" color="text" fontWeight="medium">
            {video.uploadDate}
          </MDTypography>
        ),
        tipo: getVideoBadge(video.isStatic),
        acciones: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <IconButton
              onClick={() => handlePreviewToggle(video.id)}
              color={previewPlaying === video.id ? "error" : "primary"}
              size="small"
              title="Vista previa"
            >
              {previewPlaying === video.id ? <Stop /> : <PlayArrow />}
            </IconButton>
            <IconButton
              onClick={() => handleEditVideo(video)}
              color="warning"
              size="small"
              disabled={video.isStatic}
              title={video.isStatic ? "No se puede editar videos originales" : "Editar video"}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={() => handleDeleteVideo(video)}
              color="error"
              size="small"
              disabled={video.isStatic}
              title={video.isStatic ? "No se puede eliminar videos originales" : "Eliminar video"}
            >
              <Delete />
            </IconButton>
          </MDBox>
        ),
        original: video,
      })),
    };
  };

  // Filter rows based on searchTerm
  const filteredRows = useMemo(() => {
    if (!searchTerm) {
      return tableData.rows;
    }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return tableData.rows.filter((row) => {
      const { title, filename, size } = row.original || {};
      return (
        (title && title.toLowerCase().includes(lowercasedSearchTerm)) ||
        (filename && filename.toLowerCase().includes(lowercasedSearchTerm)) ||
        (size && size.toLowerCase().includes(lowercasedSearchTerm))
      );
    });
  }, [tableData.rows, searchTerm]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Pagination logic
  const totalItems = filteredRows.length;
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    Administrador de videos para el test canciones
                  </MDTypography>

                </MDBox>
                <MDBox pt={3}>
                  <MDBox mb={2} px={3}>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setUploadDialogOpen(true)}
                      sx={{ 
                        mb: 2, 
                        color: 'white !important',
                        '& .MuiButton-startIcon': {
                          color: 'white !important'
                        }
                      }}
                    >
                      Subir nuevo video
                    </Button>
                  </MDBox>

                  <DataTable
                    table={{ columns: tableData.columns, rows: paginatedRows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                  <Pagination
                    itemsPerPage={ITEMS_PER_PAGE}
                    totalItems={totalItems}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                  />

                  {previewPlaying && (
                    <MDBox mt={3} px={3}>
                      <Card>
                        <MDBox p={3}>
                          <MDTypography variant="h6" mb={2}>
                            Preview: {videos.find(v => v.id === previewPlaying)?.title}
                          </MDTypography>
                          <VideoPlayer
                            src={videos.find(v => v.id === previewPlaying)?.url}
                            fragmentDuration={20}
                            maxPlays={2}
                            autoPlay={true}
                          />
                        </MDBox>
                      </Card>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <MDTypography variant="h5">Upload New Video</MDTypography>
          </DialogTitle>
          <DialogContent>
            <MDBox py={2}>
              <input
                type="file"
                accept="video/*"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                fullWidth
                sx={{ mb: 2 }}
                disabled={uploading}
              >
                Select Video File
              </Button>
              
              {newVideoFile && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Selected: {newVideoFile.name} ({formatFileSize(newVideoFile.size)})
                </Alert>
              )}
              
              <TextField
                fullWidth
                label="Video Title"
                value={newVideoTitle}
                onChange={(e) => setNewVideoTitle(e.target.value)}
                disabled={uploading}
                sx={{ mb: 2 }}
              />
              
              {uploading && (
                <MDBox>
                  <MDTypography variant="caption" sx={{ mb: 1, display: 'block' }}>
                    Uploading... {uploadProgress}%
                  </MDTypography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </MDBox>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUploadVideo} variant="contained" disabled={uploading || !newVideoFile}>
              Upload
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <MDTypography variant="h5">Edit Video</MDTypography>
          </DialogTitle>
          <DialogContent>
            <MDBox py={2}>
              <TextField
                fullWidth
                label="Video Title"
                value={newVideoTitle}
                onChange={(e) => setNewVideoTitle(e.target.value)}
              />
            </MDBox>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateVideo} variant="contained">Update</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>
            <MDTypography variant="h5">Confirm Delete</MDTypography>
          </DialogTitle>
          <DialogContent>
            <MDTypography variant="body1">
              Are you sure you want to delete &quot;{selectedVideo?.title}&quot;? This action cannot be undone.
            </MDTypography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDeleteVideo} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
};

export default AdminVideoPanel;